import { TOOL_COUNT } from '../../content/toolCount';
import React from 'react';
import useReveal from '../AIEmployees/useReveal';
import './ConnectorStrip.css';

/**
 * ConnectorStrip — "Works with the tools your business already runs on."
 *
 * The claim "{TOOL_COUNT} tools" is abstract; a wall of marks a visitor recognises is
 * proof. A buyer scans for THEIR tool and moves on, so this is deliberately a
 * recognition surface, not a reading one: no names, no descriptions.
 *
 * DESIGN (Nitish, Aug 21):
 *  - Chip = the /agent-to-agent treatment verbatim (rgba(255,255,255,0.06),
 *    radius 11, 26px mark) so the two logo walls on the site match.
 *  - 32 tiles in an 8-column grid = four rows, and the FOURTH ROW IS CUT AT ITS
 *    MIDPOINT by a fade. The half-row is the whole point: it makes the catalog
 *    feel deep instead of stating a number. Row 4 is really in the DOM
 *    (crawlable, no fake cut) — it just dissolves into the page.
 *  - NO "see all" CTA: there is no public catalog page to land on, and a CTA
 *    into nothing is worse than no CTA. The fade alone carries "there is more".
 *  - "{TOOL_COUNT} tools" renders in the accent teal (Nitish, Aug 23) — same
 *    treatment as the other section headings' accent line, via the token so
 *    both faces get the right teal.
 *
 * LOGOS are real brand marks bundled locally from the same source the product's
 * own connectors page renders (see scripts/fetch_connector_logos.py). Marks are
 * never recoloured: the few brands whose logo is monochrome-black ship a
 * dark-mode variant, and the fetch script takes that one so nothing disappears
 * against our surface. Salesforce is deliberately absent (their guidelines
 * require a license for logo use).
 *
 * TWO-FACE RULE (system-adaptive pages, Aug 23): the dark-variant marks that
 * the dark surface needs (GitHub, Intercom — white artwork) VANISH on the
 * light face. Those two ship a `-light.svg` twin (the brand's standard mark,
 * same CDN, ?theme default) and render BOTH imgs; MarketingAdaptiveTheme.css's
 * .mkt-face-light / .mkt-face-dark classes show exactly one per face. Marks
 * with full-colour artwork carry no class and always show.
 */

/* Slugs whose bundled mark is the brand's WHITE dark-mode artwork — each has
   a `<slug>-light.svg` twin on disk for the light face. */
const LIGHT_TWINS = new Set(['github', 'intercom']);

/* Order is deliberate: the most-recognised marks fill rows 1-2, so the value
   lands even for a visitor who never scrolls past the fade. */
const LOGOS = [
  ['gmail', 'Gmail'], ['slack', 'Slack'], ['notion', 'Notion'], ['hubspot', 'HubSpot'],
  ['stripe', 'Stripe'], ['shopify', 'Shopify'], ['googlesheets', 'Google Sheets'],
  ['googlecalendar', 'Google Calendar'], ['googledrive', 'Google Drive'],
  ['linkedin', 'LinkedIn'], ['github', 'GitHub'], ['airtable', 'Airtable'],
  ['zoom', 'Zoom'], ['canva', 'Canva'], ['intercom', 'Intercom'], ['zendesk', 'Zendesk'],
  ['asana', 'Asana'], ['trello', 'Trello'], ['jira', 'Jira'], ['calendly', 'Calendly'],
  ['mailchimp', 'Mailchimp'], ['quickbooks', 'QuickBooks'], ['youtube', 'YouTube'],
  ['twitter', 'X'], ['dropbox', 'Dropbox'], ['zapier', 'Zapier'], ['figma', 'Figma'],
  ['discord', 'Discord'], ['monday', 'Monday'], ['clickup', 'ClickUp'],
  ['typeform', 'Typeform'], ['webflow', 'Webflow'],
];

const ConnectorStrip = ({
  title = (
    <>
      Works with <span className="conn-strip__accent">{TOOL_COUNT} tools</span> your
      business already runs on.
    </>
  ),
  sub = 'Your employee works inside the tools you already pay for, with the permission level you set.',
}) => {
  const [ref, visible] = useReveal();

  return (
    <section className="conn-strip aie-section" ref={ref}>
      <div className="aie-section-head">
        <h2 className="aie-section-title aie-display">{title}</h2>
        <p className="aie-section-sub">{sub}</p>
      </div>
      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`}>
        <div className="conn-strip__fade">
          <div className="conn-strip__grid">
            {LOGOS.map(([slug, name]) => (
              <span className="conn-strip__tile" key={slug}>
                <img
                  className={LIGHT_TWINS.has(slug) ? 'mkt-face-dark' : undefined}
                  src={`/assets/icons/connectors/${slug}.svg`}
                  alt={name}
                  loading="lazy"
                  width="26"
                  height="26"
                />
                {LIGHT_TWINS.has(slug) && (
                  <img
                    className="mkt-face-light"
                    src={`/assets/icons/connectors/${slug}-light.svg`}
                    alt=""
                    loading="lazy"
                    width="26"
                    height="26"
                  />
                )}
              </span>
            ))}
          </div>
        </div>
        <p className="conn-strip__legal">
          All product names, logos, and brands are property of their respective owners.
        </p>
      </div>
    </section>
  );
};

export default ConnectorStrip;
